import {GameBoard, GameConfig, GameData, GamePlay, GamePlayKey, GameSetup} from "../types";
import {v4 as uuid} from "uuid";

export class WordmeshLayout {

  constructor(readonly level: WordmeshDifficultyLevel, readonly letters: string[], readonly targetWords: string[], readonly allWords: string[]) {
  }
}

export class WordmeshDifficultyLevel {

  static readonly VALUES = new Array<WordmeshDifficultyLevel>(3);
  static readonly EASY = WordmeshDifficultyLevel.VALUES[0] = new WordmeshDifficultyLevel("easy", "Easy", "Use all letters in 8 words", 8);
  static readonly MEDIUM = WordmeshDifficultyLevel.VALUES[1] = new WordmeshDifficultyLevel("medium", "Medium", "Use all letters in 7 words", 7);
  static readonly HARD = WordmeshDifficultyLevel.VALUES[2] = new WordmeshDifficultyLevel("hard", "Hard", "Use all letters in 6 words", 6);

  constructor(readonly name: string, readonly text: string, readonly description: string, readonly maxTries: number) {
  }
}

export class WordmeshGameConfig extends GameConfig {

  constructor(readonly level: WordmeshDifficultyLevel) {
    super();
  }
}

export class WordmeshGameSetup extends GameSetup {

  constructor(key: GamePlayKey) {
    super(key);
  }
}

export class WordmeshGameData extends GameData<WordmeshGameBoard> {

  static async create(config: WordmeshGameConfig): Promise<WordmeshGameData> {
    const loader = new WordmeshGamesLoader();
    const layout = await loader.getRandomGame(config.level);
    const setup = new WordmeshGameSetup(new GamePlayKey("wordmesh", uuid()));
    const now = Date.now();
    return new WordmeshGameData(new GamePlay(setup, now, 0, 0, 0, new WordmeshGameBoard(config.level, layout)));
  }
}

type TrieNode = {
  letter: string,
  words: string[],
  children?: TrieNode[],
}

const LETTERS = new Array(26).fill(0).map((_, i) => String.fromCharCode("A".charCodeAt(0) + i));

export class WordmeshGamesLoader {

  async getRandomGame(level: WordmeshDifficultyLevel): Promise<WordmeshLayout> {
    const allWords: string[] = (await fetch("/assets/dict/all-words.txt").then(result => result.text())).split("\n")
      .map(word => word.trim().toUpperCase())
      .filter(word => word.length >= 3);
    allWords.sort();

    const trieRoot: TrieNode = {letter: null, words: allWords, children: []};
    this.constructTrie(trieRoot, 0);

    let minimalSet: string[];
    let meshWords: string[];
    let meshLetters: string[];
    do {
      do {
        const letters = [...LETTERS];
        meshLetters = [];
        for (let i = 0; i < 12; i++) {
          meshLetters.push(letters.splice(Math.floor(Math.random() * letters.length), 1)[0]);
        }
        meshWords = [];
        for (let i = 0; i < 12; i++) {
          meshWords.push(...this.findMeshWords(trieRoot, meshLetters, i, ""));
        }
        // const uniqueLetterSet = new Set<string>();
        // for (const word of meshWords) {
        //   word.split("").map(letter => uniqueLetterSet.add(letter));
        // }
        // const sortedMeshLetters = [...meshLetters];
        // sortedMeshLetters.sort();
        // const uniqueLetters = [...uniqueLetterSet];
        // uniqueLetters.sort();
        // console.log("meshLetters", sortedMeshLetters, " ==== ", uniqueLetters);
      } while (meshWords.length <= 100 || meshWords.length >= 200 || meshLetters.length < 12);
      const randomized: string[] = [];
      while (meshWords.length > 0) {
        randomized.push(meshWords.splice(Math.floor(Math.random() * meshWords.length), 1)[0]);
      }
      meshWords = randomized;
      //console.log(meshWords);
      const minimalCoverSets = this.findMinimalCoverSets(meshWords, Date.now() + 500);
      // console.log(minimalCoverSets);
      minimalSet = minimalCoverSets[0];
      for (const set of minimalCoverSets) {
        if (set.length < minimalSet.length) {
          minimalSet = set;
        }
      }
      // console.log("minimal: " + minimalSet);
    } while (!(minimalSet?.length > 0));
    const letters = new Set<string>();
    for (const word of minimalSet) {
      word.split("").map(letter => letters.add(letter));
    }
    const uniqueLetters = [...letters];
    uniqueLetters.sort();
    // console.log(uniqueLetters);
    return new WordmeshLayout(level, meshLetters, meshWords, allWords);
  }

  private findMinimalCoverSets(words: string[], endAt: number): string[][] {
    const allCoverSets: string[][] = [];
    let maxLevel = 6;
    const minLevel = 4;
    for (let i = 0; i < words.length; i++) {
      const word = words[i];
      const coverSet = this.findMinimalCoverSetInternal(words, 1, maxLevel, [word], endAt);
      if (Date.now() >= endAt) {
        return allCoverSets;
      }
      if (coverSet) {
        if (coverSet.length < maxLevel) {
          maxLevel = coverSet.length;
        }
        allCoverSets.push(coverSet);
        if (coverSet.length >= minLevel) {
          break;
        }
      }
    }
    return allCoverSets;
  }

  private findMinimalCoverSetInternal(words: string[], level: number, maxLevel: number, setSoFar: string[], endAt: number): string[] {
    const letters = new Set<string>();
    for (const word of setSoFar) {
      word.split("").map(letter => letters.add(letter));
    }
    if (letters.size === 12) { // All letters are used
      return setSoFar;
    }
    if (level >= maxLevel) {
      return null;
    }
    if (Date.now() >= endAt) {
      return null;
    }
    const lastWord = setSoFar[setSoFar.length - 1];
    const lastLetter = lastWord.substring(lastWord.length - 1);
    const allowedWords = words.filter(word => word.substring(0, 1) === lastLetter);
    for (let i = 0; i < allowedWords.length; i++) {
      const word = allowedWords[i];
      if (setSoFar.includes(word)) {
        continue;
      }
      const minimalCoverSet = this.findMinimalCoverSetInternal(words, level + 1, maxLevel, [...setSoFar, word], endAt);
      if (minimalCoverSet) {
        return minimalCoverSet;
      }
    }
    return null;
  }

  private findMeshWords(trieNode: TrieNode, meshLetters: string[], index: number, wordSoFar: string): string[] {
    const words = trieNode.words;
    if (index > 0 && !words.find(word => word.startsWith(wordSoFar))) {
      return [];
    }
    const group = Math.floor(index / 3);
    const letter = meshLetters[index];
    wordSoFar = wordSoFar + letter;
    const meshWords = [];
    if (wordSoFar.length >= 3 && words.includes(wordSoFar)) {
      meshWords.push(wordSoFar);
    }
    for (let i = 0; i < 4; i++) {
      if (i === group) {
        continue;
      }
      for (let j = 0; j < 3; j++) {
        meshWords.push(...this.findMeshWords(trieNode.children?.[letter.charCodeAt(0) - "A".charCodeAt(0)] || trieNode, meshLetters, i * 3 + j, wordSoFar));
      }
    }
    return meshWords;
  }

  private constructTrie(trieNode: TrieNode, index: number) {
    if (index >= 3) {
      return;
    }
    LETTERS.forEach(letter => this.constructTrieForLetter(trieNode, index, letter));
  }

  private constructTrieForLetter(trieNode: TrieNode, index: number, letter: string): void {
    const char = letter.charCodeAt(0);
    const words = trieNode.words.filter(word => word.charCodeAt(index) === char);
    const childNode = {letter, words, children: []};
    trieNode.children.push(childNode);
    this.constructTrie(childNode, index + 1);
  }
}

// This code is generated by Claude but it doesnt't work in the general case where letters are randomly generated.
// Keeping it here for records purpose, but we shouldn't use it without first fixing the errors.
export class WordmeshGamesLoaderClaudeGen {

  async getRandomGame(level: WordmeshDifficultyLevel): Promise<WordmeshLayout> {
    // Load dictionary
    const response = await fetch("/assets/dict/all-words.txt");
    const dictText = await response.text();
    const allWords = dictText.split('\n')
      .map(word => word.trim().toUpperCase())
      .filter(word => word.length >= 3);
    const {letters, targetWords} = await this.generateSetup(allWords);
    return new WordmeshLayout(level, letters.flat(), targetWords, allWords);
  }

  // Wordmesh is a word puzzle where you must use all the letters around a square (three on each side) to form words. Words must be at least three letters long, and the last letter of one word must be the first letter of the next word. Consecutive letters in a word cannot come from the same side of the square. The goal is to use all the letters in as few words as possible.
  // The Setup:
  //   You are presented with a square grid of letters, with three letters on each side.
  // Forming Words:
  //   You must create words using these letters, with a minimum length of three letters.
  // Side Constraint:
  //   You cannot use letters from the same side of the square consecutively in a word.
  // Word Chain:
  //   The last letter of one word becomes the first letter of the next word.
  // Objective:
  //   Use all the letters at least once, and try to complete the puzzle in the fewest words possible.
  // Fill out this method which generates a list of random letters and a list of target words that can be made using the
  // rules above. The target words must come from the dictionary located at "/assets/dict/all-words.txt".
  async generateSetup(allWords: string[]): Promise<{ letters: string[][], targetWords: string[] }> {
    // Generate letter arrangement - 4 sides with 3 letters each
    const letters = this.generateLetterArrangement();

    // Find valid words that can be formed
    const validWords = this.findValidWords(letters, allWords);

    // Find a solution that uses all letters
    const targetWords = this.findTargetWords(letters, validWords);

    return {letters, targetWords};
  }

  private generateLetterArrangement(): string[][] {
    // Use predefined.
    // Common letter combinations that work well.
    const commonLetterSets = [
      [['T', 'R', 'A'], ['N', 'E', 'S'], ['I', 'O', 'L'], ['D', 'C', 'U']],
      [['S', 'H', 'A'], ['R', 'E', 'D'], ['T', 'I', 'N'], ['G', 'O', 'L']],
      [['B', 'L', 'A'], ['C', 'K', 'E'], ['R', 'S', 'T'], ['I', 'N', 'O']],
      [['P', 'R', 'A'], ['Y', 'E', 'R'], ['S', 'T', 'I'], ['N', 'G', 'O']],
      [['F', 'L', 'O'], ['W', 'E', 'R'], ['S', 'T', 'A'], ['I', 'N', 'G']]
    ];
    return commonLetterSets[Math.floor(Math.random() * commonLetterSets.length)];

    // Use random
    // const letters = [...LETTERS];
    // const meshLetters: string[][] = [];
    // for (let i = 0; i < 4; i++) {
    //   const sideLetters: string[] = [];
    //   for (let j = 0; j < 3; j++) {
    //     sideLetters.push(letters.splice(Math.floor(Math.random() * letters.length), 1)[0]);
    //   }
    //   meshLetters.push(sideLetters);
    // }
    // return meshLetters;
  }

  private findValidWords(letters: string[][], dictionary: string[]): string[] {
    const allLetters = letters.flat();
    const letterToSide = new Map<string, number>();

    // Map each letter to its side
    letters.forEach((side, sideIndex) => {
      side.forEach(letter => {
        letterToSide.set(letter, sideIndex);
      });
    });

    return dictionary.filter(word => {
      return this.isValidWord(word, letterToSide, allLetters);
    });
  }

  private isValidWord(word: string, letterToSide: Map<string, number>, allLetters: string[]): boolean {
    if (word.length < 3) return false;

    // Check if all letters in word are available
    for (const letter of word) {
      if (!allLetters.includes(letter)) return false;
    }

    // Check side constraint - no consecutive letters from same side
    for (let i = 0; i < word.length - 1; i++) {
      const currentSide = letterToSide.get(word[i]);
      const nextSide = letterToSide.get(word[i + 1]);
      if (currentSide === nextSide) return false;
    }

    return true;
  }

  private findTargetWords(letters: string[][], validWords: string[]): string[] {
    const allLetters = new Set(letters.flat());

    // Try to find a solution with 2-4 words that uses all letters
    for (let wordCount = 2; wordCount <= 4; wordCount++) {
      const solution = this.findSolutionWithWordCount(validWords, allLetters, wordCount);
      if (solution.length > 0) {
        return solution;
      }
    }

    // Fallback - return some good words that use many letters
    return this.getFallbackWords(validWords, allLetters);
  }

  private findSolutionWithWordCount(validWords: string[], allLetters: Set<string>, targetCount: number): string[] {
    // Simple greedy approach to find a solution
    const solution: string[] = [];
    const usedLetters = new Set<string>();
    let lastLetter = '';

    // Sort words by length (longer words first) to cover more letters
    const sortedWords = validWords.sort((a, b) => b.length - a.length);

    for (const word of sortedWords) {
      if (solution.length >= targetCount) break;

      // Check if this word can continue the chain
      if (lastLetter && word[0] !== lastLetter) continue;

      // Check if this word adds new letters
      const wordLetters = new Set(word);
      const newLetters = Array.from(wordLetters).filter(letter => !usedLetters.has(letter));

      if (newLetters.length > 0 || solution.length === 0) {
        solution.push(word);
        wordLetters.forEach(letter => usedLetters.add(letter));
        lastLetter = word[word.length - 1];

        // If we've used all letters, we're done
        if (usedLetters.size === allLetters.size) {
          return solution;
        }
      }
    }

    // Check if this solution uses all letters
    return usedLetters.size === allLetters.size ? solution : [];
  }

  private getFallbackWords(validWords: string[], allLetters: Set<string>): string[] {
    // Return the longest words that use the most letters
    return validWords
      .sort((a, b) => {
        const aUnique = new Set(a).size;
        const bUnique = new Set(b).size;
        return bUnique - aUnique || b.length - a.length;
      })
      .slice(0, 3);
  }
}

export class WordmeshGameBoard extends GameBoard {

  readonly foundWords: string[] = [];

  constructor(readonly level: WordmeshDifficultyLevel, readonly layout: WordmeshLayout) {
    super();
  }

  getPoints(duration: number): number {
    const basePoints = this.foundWords.length * 100;
    const bonusPoints = this.layout.targetWords.every(word => this.foundWords.includes(word)) ? 500 : 0;
    const timeBonus = Math.max(0, 300 - Math.floor(duration / 1000));
    return basePoints + bonusPoints + timeBonus;
  }

  isComplete(): boolean {
    const uniqueLetterSet = new Set<string>();
    for (const word of this.foundWords) {
      word.split("").map(letter => uniqueLetterSet.add(letter));
    }
    return uniqueLetterSet.size === 12;
  }

  isValidWord(word: string): boolean {
    return this.layout.allWords.includes(word.toUpperCase()) && !this.foundWords.includes(word.toUpperCase());
  }

  addWord(word: string): boolean {
    if (this.isValidWord(word)) {
      this.foundWords.push(word);
      return true;
    }
    return false;
  }

  toJSON(): any {
    return this;
  }
}